import React, { useState } from 'react';
import { BOTTOM_INDENTS, COLORS } from '@constants';
import { PLATFORM_NAME } from '@enums';
import { useConnector, useStore } from '@hooks';
import { Button, Divider, Icon, Loader, Typography, WhiteCover } from '@ui';
import { formatString } from '@utils';
import { Spin } from 'antd';
import { observer } from 'mobx-react';
import { SETUP_KEYS } from '../../constants';
import SubdomainCollapse from '../../modals/SubdomainCollapse';
import LineItem from '../../shared/LineItem';
import CoverTitle from '../CoverTitle';
import { StyledCollapseContent } from '../StoreSetupManual/styled';
import Connected from './Connected/index';
import NotConnected from './NotConnected/index';
import ThemeExtensionStatus from './ThemeExtensionStatus/index';

const IntegrationShopify = observer(({ storeUrl, isValid = false, setModal, modal, onModalClose }) => {
    const trimmedDomain = storeUrl.replace(/\.myshopify\.com$/, '');
    const store = useStore();
    const { getEcommerceRedirect, isFormPending } = store.authStore;
    const { companyName, setupInfo } = store.accountsStore;

    const { connected } = useConnector();

    const [analyticsScriptOpen, setAnalyticsScriptOpen] = useState(false);
    const [purchaseEventOpen, setPurchaseEventOpen] = useState(false);

    const updateScopes = () => {
        getEcommerceRedirect(PLATFORM_NAME.SHOPIFY, {
            companyName,
            shop: storeUrl,
            token_type: 'offline'
        });
    };

    const setupConfig = {
        [SETUP_KEYS.ANALYTICS_SCRIPT]: {
            title: 'Analytics script setup',
            status: setupInfo.scriptTag?.status,
            isManual: false
        },
        [SETUP_KEYS.PURCHASE_EVENT]: {
            title: 'Purchase events setup',
            status: setupInfo.purchaseEventStatus,
            isManual: false
        }
    };

    return (
        <Spin spinning={isFormPending} indicator={<Loader />}>
            <WhiteCover
                data-e2e='integrations_connector-app_section'
                customTitle={<CoverTitle title='Shopify' icon={<Icon.ShopifyLogo />} />}
                style={BOTTOM_INDENTS.M}
                rightContent={
                    <Button
                        data-e2e='integrations_go-to-connector-app_link'
                        type='link'
                        padding='0'
                        onClick={() => {
                            window.open(`https://${storeUrl}/admin`, '_blank');
                        }}
                    >
                        Go to my Shopify store
                    </Button>
                }
            >
                <Typography.Text
                    data-e2e='integrations_connector-app_description'
                    type='body2'
                    color={COLORS.$gray60Black}
                    style={BOTTOM_INDENTS.M}
                >
                    Sync Shopify to manage inventory, product details and fulfill orders all from Shopify.
                </Typography.Text>
                {connected ? (
                    <Connected
                        setupInfo={setupInfo}
                        isValid={isValid}
                        trimmedDomain={trimmedDomain}
                        updateScopes={updateScopes}
                    />
                ) : (
                    <NotConnected storeUrl={storeUrl} />
                )}

                <ThemeExtensionStatus
                    themeExtension={setupInfo.themeExtension}
                    isValid={setupInfo.themeExtension?.hasRequiredScopes}
                />

                {Object.entries(setupConfig).map(([key, data]) => {
                    const isAnalyticsScript = key === SETUP_KEYS.ANALYTICS_SCRIPT;
                    const isPurchaseEvent = key === SETUP_KEYS.PURCHASE_EVENT;
                    const isOpen = isAnalyticsScript
                        ? analyticsScriptOpen
                        : isPurchaseEvent
                        ? purchaseEventOpen
                        : false;
                    const toggleCollapse = isAnalyticsScript
                        ? () => setAnalyticsScriptOpen(!analyticsScriptOpen)
                        : isPurchaseEvent
                        ? () => setPurchaseEventOpen(!purchaseEventOpen)
                        : () => {};

                    return (
                        <div
                            key={key}
                            style={BOTTOM_INDENTS.S}
                            data-e2e={`integrations_${formatString(data.title)}_block`}
                        >
                            <LineItem
                                setModal={setModal}
                                modal={modal}
                                setupKey={key}
                                data={data}
                                onModalClose={onModalClose}
                                isCollapsible
                                isOpen={isOpen}
                                onToggle={toggleCollapse}
                                bottomMargin={{ marginBottom: 0 }}
                                style={
                                    isOpen
                                        ? {
                                              borderBottomLeftRadius: 0,
                                              borderBottomRightRadius: 0
                                          }
                                        : {}
                                }
                            />
                            {isAnalyticsScript && analyticsScriptOpen && (
                                <StyledCollapseContent>
                                    <Divider margin='0' />
                                    <SubdomainCollapse
                                        openedCollapse={SETUP_KEYS.ANALYTICS_SCRIPT}
                                        initialDomains={setupInfo.restCustomDomains || []}
                                    />
                                </StyledCollapseContent>
                            )}
                            {isPurchaseEvent && purchaseEventOpen && (
                                <StyledCollapseContent>
                                    <Divider margin='0' />
                                    <SubdomainCollapse
                                        openedCollapse={SETUP_KEYS.PURCHASE_EVENT}
                                        initialDomains={setupInfo.restCustomDomains || []}
                                    />
                                </StyledCollapseContent>
                            )}
                        </div>
                    );
                })}
            </WhiteCover>
        </Spin>
    );
});

export default IntegrationShopify;
